/**
 * TransactionHistory — Lịch sử credit transactions
 */

import React from 'react';
import { ArrowDownCircle, ArrowUpCircle, Gift, RotateCcw, Coins } from 'lucide-react';
import { useCredits } from '../../hooks/useCredits';
import { formatCredits } from '../../services/mock/creditMock';
import type { TransactionType } from '../../types/credit';

const TYPE_CONFIG: Record<TransactionType, { icon: React.ElementType; color: string; label: string }> = {
  purchase: { icon: ArrowDownCircle, color: 'text-emerald-600 bg-emerald-50', label: 'Mua credits' },
  consume: { icon: ArrowUpCircle, color: 'text-red-500 bg-red-50', label: 'Sử dụng' },
  welcome: { icon: Gift, color: 'text-indigo-600 bg-indigo-50', label: 'Tặng miễn phí' },
  bonus: { icon: Gift, color: 'text-amber-600 bg-amber-50', label: 'Bonus' },
  refund: { icon: RotateCcw, color: 'text-blue-600 bg-blue-50', label: 'Hoàn trả' },
};

const TransactionHistory: React.FC = () => {
  const { transactions } = useCredits();

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Coins className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Chưa có giao dịch nào</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-2">
      {transactions.map((tx) => {
        const config = TYPE_CONFIG[tx.type];
        const Icon = config.icon;
        const isPositive = tx.credits > 0;
        const timeStr = new Date(tx.createdAt).toLocaleString('vi-VN', {
          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });

        return (
          <div
            key={tx.id}
            className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
          >
            {/* Icon */}
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}>
              <Icon className="w-4 h-4" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-400 uppercase">{config.label}</span>
              </div>
              <p className="text-sm text-slate-700 truncate">{tx.reason}</p>
            </div>

            {/* Amount + balance */}
            <div className="text-right shrink-0">
              <p className={`text-sm font-bold font-mono ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                {isPositive ? '+' : ''}{formatCredits(tx.credits)}
              </p>
              <p className="text-[10px] text-slate-400 font-mono">
                Còn {formatCredits(tx.balanceAfter)}
              </p>
            </div>

            {/* Time */}
            <span className="text-[10px] text-slate-400 shrink-0 w-16 text-right">
              {timeStr}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default TransactionHistory;
